import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

/** Legacy /glossary/:slug → /methodology/glossary/:slug (permanent relocation). */
const LegacyGlossaryRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/methodology/glossary/${slug ?? ""}`} replace />;
};
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { ModelConfigProvider } from "@/contexts/ModelConfigContext";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { isAnalyticsSuppressed, redactAnalyticsUrl } from "@/lib/analytics";
import { CookieConsent } from "@/components/consent/CookieConsent";
import { isAuthError } from "@/lib/auth-utils";
import NotFound from "./pages/NotFound";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import ProtectedRoute from "./components/ProtectedRoute";
import PageLoadingFallback from "./components/layout/PageLoadingFallback";
import AnalyticsDashboard from "./pages/admin/AnalyticsDashboard";
import SentryErrorFallback from "./components/SentryErrorFallback";
import SentryUserSync from "./components/SentryUserSync";
import ScrollToTop from "./components/ScrollToTop";
import SuperAdminAnalyticsSync from "./components/SuperAdminAnalyticsSync";
import { SubscriptionGate } from "@/components/auth/SubscriptionGate";

// Lazy-loaded page components
const Index = lazy(() => import("./pages/Index"));
const Welcome = lazy(() => import("./pages/Welcome"));
const LandingVariantB = lazy(() => import("./pages/LandingVariantB"));
const UseCases = lazy(() => import("./pages/UseCases"));
const Features = lazy(() => import("./pages/Features"));
const WinYourCategories = lazy(() => import("./pages/WinYourCategories"));
const ProcurementAiSystems = lazy(() => import("./pages/ProcurementAiSystems"));
const GlossaryIndex = lazy(() => import("./pages/GlossaryIndex"));
const GlossaryTerm = lazy(() => import("./pages/GlossaryTerm"));

const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const AiDisclaimer = lazy(() => import("./pages/AiDisclaimer"));
const Dpa = lazy(() => import("./pages/Dpa"));
const FAQ = lazy(() => import("./pages/FAQ"));
const GeneratedReport = lazy(() => import("./pages/GeneratedReport"));
const SharedReport = lazy(() => import("./pages/SharedReport"));
const ReportLibrary = lazy(() => import("./pages/ReportLibrary"));
const SavedReportViewer = lazy(() => import("./pages/SavedReportViewer"));
const DashboardShowcase = lazy(() => import("./pages/DashboardShowcase"));
const MarketIntelligence = lazy(() => import("./pages/MarketIntelligence"));
const Projects = lazy(() => import("./pages/Projects"));
const NewProject = lazy(() => import("./pages/NewProject"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const TestingPipeline = lazy(() => import("./pages/TestingPipeline"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Account = lazy(() => import("./pages/Account"));
const ChoosePlan = lazy(() => import("./pages/ChoosePlan"));
const FounderDashboard = lazy(() => import("./pages/admin/FounderDashboard"));
const RiskPlatform = lazy(() => import("./pages/enterprise/RiskPlatform"));
const RiskPlatformV2 = lazy(() => import("./pages/enterprise/RiskPlatformV2"));
const InflationPlatform = lazy(() => import("./pages/enterprise/InflationPlatform"));
const InflationPlatformV2 = lazy(() => import("./pages/enterprise/InflationPlatformV2"));
const PdfTestPage = lazy(() => import("./pages/PdfTestPage"));
const MethodologyDashboard = lazy(() => import("./pages/admin/MethodologyDashboard"));
const MethodologyScenarioEdit = lazy(() => import("./pages/admin/MethodologyScenarioEdit"));
const MethodologyConfig = lazy(() => import("./pages/admin/MethodologyConfig"));
const MethodologyHistory = lazy(() => import("./pages/admin/MethodologyHistory"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const MethodologyIndex = lazy(() => import("./pages/MethodologyIndex"));
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Scenario landing pages
const TCOAnalysis = lazy(() => import("./pages/scenarios/TCOAnalysis"));
const SupplierRisk = lazy(() => import("./pages/scenarios/SupplierRisk"));
const NegotiationPrep = lazy(() => import("./pages/scenarios/NegotiationPrep"));
const MakeOrBuy = lazy(() => import("./pages/scenarios/MakeOrBuy"));
const BlackSwan = lazy(() => import("./pages/scenarios/BlackSwan"));

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error) => {
        if (!isAuthError(error)) {
          Sentry.captureException(error, {
            tags: { source: "react-query-mutation" },
          });
        }
      },
    },
  },
});

const App = () => {
  return (
  <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>
  <HelmetProvider>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <ModelConfigProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SentryUserSync />
          <SuperAdminAnalyticsSync />
          <Toaster />
          <Sonner />
          <CookieConsent />
          <BrowserRouter>
          <ScrollToTop />
          {/* Vercel Web Analytics + Speed Insights are cookieless, so they load
              for all visitors (no consent gate). GA4, which uses cookies, stays
              gated via Google Consent Mode v2 in gtag-init.js + the cookie banner.
              Super admins are excluded from all channels: the beforeSend guards
              below drop their beacons (incl. custom track() events — beforeSend
              sees both pageviews and events), SuperAdminAnalyticsSync persists
              the flag, and gtag-init.js hard-disables GA4 for them at boot. */}
          <Analytics
            beforeSend={(event) => {
              if (isAnalyticsSuppressed()) return null;
              const url = redactAnalyticsUrl(event.url);
              return url === null ? null : { ...event, url };
            }}
          />
          <SpeedInsights
            sampleRate={1}
            beforeSend={(data) => {
              if (isAnalyticsSuppressed()) return null;
              const url = redactAnalyticsUrl(data.url);
              return url === null ? null : { ...data, url };
            }}
          />
          <div className="pb-14 md:pb-0 overflow-x-clip">
          <Suspense fallback={<PageLoadingFallback />}>
          <SubscriptionGate>
          <Routes>
            <Route path="/welcome" element={<Welcome />} />
            {/* The second welcome is the default since 2026-10-07 (client): /welcome renders it; old /welcome2 links land there. */}
            <Route path="/welcome2" element={<Navigate to="/welcome" replace />} />
            <Route path="/win-your-categories" element={<WinYourCategories />} />
            <Route path="/procurement-risk-platform" element={<LandingVariantB />} />
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/" element={<Index />} />
            <Route path="/analyse/:scenarioSlug" element={<Index />} />
            <Route path="/features" element={<Features />} />
            <Route path="/reports" element={<Features />} />
            <Route path="/methodology/ai-systems-classification" element={<ProcurementAiSystems />} />
            <Route path="/methodology/glossary" element={<GlossaryIndex />} />
            <Route path="/methodology/glossary/:slug" element={<GlossaryTerm />} />
            {/* Legacy reference routes — permanently relocated under /methodology */}
            <Route path="/procurement-ai-systems" element={<Navigate to="/methodology/ai-systems-classification" replace />} />
            <Route path="/glossary" element={<Navigate to="/methodology/glossary" replace />} />
            <Route path="/glossary/:slug" element={<LegacyGlossaryRedirect />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/ai-disclaimer" element={<AiDisclaimer />} />
            <Route path="/dpa" element={<Dpa />} />
            <Route path="/faq" element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
            <Route path="/report" element={<ProtectedRoute><GeneratedReport /></ProtectedRoute>} />
            <Route path="/report-library" element={<ProtectedRoute><ReportLibrary /></ProtectedRoute>} />
            <Route path="/report-library/:reportId" element={<ProtectedRoute><SavedReportViewer /></ProtectedRoute>} />
            <Route path="/report/:shareId" element={<SharedReport />} />
            <Route path="/dashboards" element={<ProtectedRoute requireSuperAdmin><DashboardShowcase /></ProtectedRoute>} />
            <Route path="/market-intelligence" element={<ProtectedRoute><Navigate to="/market-intelligence/queries" replace /></ProtectedRoute>} />
            <Route path="/market-intelligence/queries" element={<ProtectedRoute><MarketIntelligence /></ProtectedRoute>} />
            <Route path="/market-intelligence/insights" element={<ProtectedRoute><MarketIntelligence /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/projects/new" element={<ProtectedRoute><NewProject /></ProtectedRoute>} />
            <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/testing-pipeline" element={<ProtectedRoute requireSuperAdmin><TestingPipeline /></ProtectedRoute>} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/choose-plan" element={<ProtectedRoute><ChoosePlan /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute requireSuperAdmin><FounderDashboard /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute requireSuperAdmin><AnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/enterprise/risk" element={<ProtectedRoute><RiskPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/risk/monitor/:id" element={<ProtectedRoute><RiskPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/inflation" element={<ProtectedRoute><Navigate to="/enterprise/inflation/dashboard" replace /></ProtectedRoute>} />
            <Route path="/enterprise/inflation/dashboard" element={<ProtectedRoute><InflationPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/inflation/setup" element={<ProtectedRoute><InflationPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/inflation/events" element={<ProtectedRoute><InflationPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/inflation/tracker/:id" element={<ProtectedRoute><InflationPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/inflation-v2" element={<ProtectedRoute><Navigate to="/enterprise/inflation-v2/dashboard" replace /></ProtectedRoute>} />
            <Route path="/enterprise/inflation-v2/dashboard" element={<ProtectedRoute><InflationPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/inflation-v2/setup" element={<ProtectedRoute><InflationPlatformV2 /></ProtectedRoute>} />
            <Route path="/enterprise/inflation-v2/tracker/:id" element={<ProtectedRoute><InflationPlatformV2 /></ProtectedRoute>} />
            <Route path="/pdf-test" element={<ProtectedRoute requireSuperAdmin><PdfTestPage /></ProtectedRoute>} />
            <Route path="/admin/methodology" element={<ProtectedRoute requireSuperAdmin><MethodologyDashboard /></ProtectedRoute>} />
            <Route path="/admin/methodology/config" element={<ProtectedRoute requireSuperAdmin><MethodologyConfig /></ProtectedRoute>} />
            <Route path="/admin/methodology/history" element={<ProtectedRoute requireSuperAdmin><MethodologyHistory /></ProtectedRoute>} />
            <Route path="/admin/methodology/:slug" element={<ProtectedRoute requireSuperAdmin><MethodologyScenarioEdit /></ProtectedRoute>} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/methodology" element={<MethodologyIndex />} />
            <Route path="/methodology/:slug" element={<MethodologyPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Scenario SEO landing pages */}
            <Route path="/scenarios/tco-analysis" element={<TCOAnalysis />} />
            <Route path="/scenarios/supplier-risk-assessment" element={<SupplierRisk />} />
            <Route path="/scenarios/negotiation-preparation" element={<NegotiationPrep />} />
            <Route path="/scenarios/make-or-buy-analysis" element={<MakeOrBuy />} />
            <Route path="/scenarios/black-swan-simulation" element={<BlackSwan />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </SubscriptionGate>
          </Suspense>
          </div>
          <MobileBottomNav />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ModelConfigProvider>
  </ThemeProvider>
  </HelmetProvider>
  </Sentry.ErrorBoundary>
  );
};

export default App;
